import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  mergeApplicationConfig,
  provideAppInitializer,
  provideStabilityDebugging
} from '@angular/core';
import {AnalyticsService} from "./services/analytics.service";
import {StorageService} from "./services/storage.service";
import {AuthService} from "./services/auth/auth.service";
import {BrowserAnalyticsService} from "./services/analytics.browser.service";
import {BrowserStorageService} from "./services/storage.browser.service";
import {BrowserAuthService} from "./services/auth/auth.browser.service";
import {sharedAppConfig} from "./app.config.shared";
import {GlobalErrorHandle} from "./global-error-handler";
import {provideNgxStripe} from "ngx-stripe";
import {KillSwitchService} from "./services/kill-switch/kill-switch.service";
import {PushNotificationService} from "./services/push-notification.service";
import {ApplicationService} from "./services/application.service";
import {BackNavigationService} from "./services/back-navigation.service";
import {BrowserBackNavigationService} from "./services/back-navigation.browser.service";
import {PaymentService} from "./services/payment/payment.service";
import {ResumeService} from "./services/resume.service";
import {App, URLOpenListenerEvent} from "@capacitor/app";
import {Router} from "@angular/router";
import {DataUpdateService} from "./services/data-update.service";
import {ClientErrorLoggingService} from "./services/client-error-logging.service";

export const browserAppConfig: ApplicationConfig = mergeApplicationConfig(sharedAppConfig, {
  providers: [
    {provide: AnalyticsService, useClass: BrowserAnalyticsService},
    {provide: StorageService, useClass: BrowserStorageService},
    {provide: AuthService, useClass: BrowserAuthService},
    {provide: BackNavigationService, useClass: BrowserBackNavigationService},
    {provide: ErrorHandler, useClass: GlobalErrorHandle},
    provideStabilityDebugging(),
    provideAppInitializer(
      () => inject(ClientErrorLoggingService).initialize(),
    ),
    provideAppInitializer(
      () => inject(AnalyticsService).initialize(),
    ),
    provideAppInitializer(
      () => inject(ApplicationService).initialize(),
    ),
    provideAppInitializer(
      () => inject(ResumeService).initialize(),
    ),
    provideAppInitializer(
      () => {
        if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then(registrations => {
            for (const registration of registrations) {
              registration.unregister();
            }
          });
        }
      },
    ),
    provideAppInitializer(
      () => {
          const router = inject(Router);
          const dataUpdateService = inject(DataUpdateService);
          let lastHandled: string | null = null;

          const handleUrl = async (rawUrl: string) => {
            if (rawUrl === lastHandled) return;
            lastHandled = rawUrl;

            try {
              const url = new URL(rawUrl);

              // The OAuth provider redirects to io.myhomey.app://callback (host === 'callback').
              // The GenericOAuth2 plugin handles that exchange inline (socialLogin); navigating
              // here would route to '/' (host 'callback' has no pathname) and flash the logged-out
              // landing mid-login. Leave the callback to the plugin.
              if (url.host === 'callback') {
                return;
              }

              await router.navigate([url.pathname], {queryParams: Object.fromEntries(url.searchParams)});
              await dataUpdateService.updateData({force: true});

              setTimeout(() => lastHandled = null, 5000);
            } catch (e) {
              console.warn('Could not parse incoming URL', e);
            }
          };

          // 1) listener (warm/resume)
          App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            void handleUrl(event.url);
          });

          // 2) cold start (initial launch url) + safe against duplicates
          void App.getLaunchUrl().then((launch) => {
            if (launch?.url) {
              void handleUrl(launch.url);
            }
          });
      }),
    provideNgxStripe(),
    provideAppInitializer(
      () => inject(KillSwitchService).initialize(),
    ),
    provideAppInitializer(
      () => inject(PushNotificationService).initialize(),
    ),
    provideAppInitializer(
      () => inject(BackNavigationService).initialize(),
    ),
    provideAppInitializer(
      () => inject(PaymentService).initialize(),
    ),
  ],
});
